import { Config } from "@/app/config";
import { catchCalculatorError, resultListFilter } from "@/component/assets/AbstractListingCalculator/domain";
import type { MarketingFilter, ResultListResponse } from "@/component/assets/AbstractListingCalculator/types";
import { CalculatorError } from "@/component/assets/CalculatorError";
import type { StringPair } from "@/shared/lib/JsonDataBuilder";
import { JsonDataBuilder } from "@/shared/lib/JsonDataBuilder";
import type { FormSelectOptions } from "@/shared/ui";
import { http } from "@/shared/ui";
import { formatText } from "@/shared/ui/format";
import type { HomeSavingsCalcResponse } from "../LeadForm/HomeSavingsLeadForm";
import type { HomeSavingsCalculatorRequest, HomeSavingsResponseItem, HomeSavingsSearchParams } from "./types";

export async function calculate(
	request: HomeSavingsCalculatorRequest,
	filter?: MarketingFilter,
): Promise<ResultListResponse<HomeSavingsResponseItem>> {
	return http
		.get(
			Config.getApiUrl("/api/public/building-society", {
				...request,
				roleCode: "BM",
				focusOn: true,
				notMakeUseOfConnectedProduct: request.notMakeUseOfConnectedProduct,
				makeUseOfConnectedProduct: !request.notMakeUseOfConnectedProduct,
				interestedInMortgage: false,
			}),
		)
		.then((results: any) => {
			if (results.message) {
				throw new CalculatorError(
					results.message,
					results.fieldErrors,
					request,
					Config.getApiUrl("/api/public/building-society"),
				);
			}

			return resultListFilter(results, filter);
		})
		.catch(catchCalculatorError);
}

export function uniqueByBankCode(list: HomeSavingsResponseItem[]): HomeSavingsResponseItem[] {
	const uniqueItems = new Map<string, HomeSavingsResponseItem>();
	list.forEach((item) => uniqueItems.has(item.bank.code) || uniqueItems.set(item.bank.code, item));

	return Array.from(uniqueItems.values());
}

export function responseToResult(
	_request: HomeSavingsCalculatorRequest,
	response: any,
): ResultListResponse<HomeSavingsResponseItem> {
	return response;
}

export function getLeadCalculatorResponse(response: HomeSavingsResponseItem): HomeSavingsCalcResponse {
	return {
		buildingSocietyName: response?.name,
		productName: response?.name,
		id: response.id,
		bankCode: response?.bank?.code,
		bankName: response?.bank?.name,
	};
}

export function getLeadCalculatorRequest(searchParams: HomeSavingsSearchParams): HomeSavingsCalculatorRequest {
	return {
		...searchParams,
		focusOn: true,
		marketingNeeded: true,
		comparator: "AER",
	};
}

export function getMonthlyDepositOptions(): FormSelectOptions<number> {
	const data: FormSelectOptions<number> = [];

	for (let i = 1; i <= 10; i++) {
		const currentValue = i * 10_000;
		data.push({ label: formatText(currentValue, "ft"), value: currentValue });
	}

	return data;
}

export const HomeSavingsInfo = {
	getSearchParametersText: (parameters: Record<string, any>): StringPair[] => {
		const data = new JsonDataBuilder();

		data.addTextItem("Megtakarítás", parameters.monthlyDeposit, "ft");
		data.addTextItem("Futamidő", parameters.depositDurationYear, "év");
		data.addTextItem("Hitellel együtt", parameters.focusOn ? "igen" : "nem");
		data.addTextItem("Plusz termék", parameters.notMakeUseOfConnectedProduct ? "igen" : "nem");

		return data.getDataArray();
	},

	getProductParametersText: (_parameters: Record<string, any>, result: Record<string, any>): StringPair[] => {
		const data = new JsonDataBuilder();
		data.addTextItem("Teljes megtakarítás", result.fullSavingsNoLoan, "ft");
		data.addTextItem("Betéti hozam (EBKM)", result.aer, "%");
		data.addTextItem("Számlanyitási díj", result.fullPayableAmount, "ft");
		data.addTextItem("Megtakarítási idő", result.savingsPeriod, "hónap");

		if (result.focusOn === true) {
			data.addTextItem("Felvehető lakáskölcsön", result.mortgageAmount, "ft");
			data.addTextItem("THM", result.apr, "%");
			data.addTextItem("Havi törlesztő", result.mortgageInstallment, "ft");
			data.addTextItem("Hiteltörlesztési futamidő", result.mortgageDuration, "hónap");
		}

		return data.getDataArray();
	},
};
